import { ProductGovernanceDocuments } from 'components/common-form/sidePanel/docToolbar/regulatory/documents/ProductGovernanceDocuments';
import { FormattedMessage } from 'components/shared/Intl';
import { LazyFallback } from 'components/shared/LazyFallback';
import { useToggleFeature } from 'hooks/useToggleFeature';
import type { ProductGovernance } from 'models/streaming';
import { FC, Suspense, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IRegulatoryRejection, IRegulatoryResource } from 'state/regulatoryDocuments';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import { ProductGovernanceDocumentsLegacy } from '../docToolbar/regulatory/documents-legacy/ProductGovernanceDocuments';
import { DocumentRejectionErrorPanel, RegulatoryComponentWrapper } from './Components';
import { MifidLazy } from './Mifid2';
import { ValueForMoney } from './ValueForMoney/ValueForMoney';

interface ProductGovernanceRegulatoryComponentProps {
  tabId: string;

  workspaceType: QuoteWorkspaceType;
  isProductGovernanceLoading: boolean;
  isInError: boolean;
  isProductGovernanceAssessmentInError: boolean;

  productGovernanceDocumentation?: IRegulatoryResource[];
  productGovernanceDocumentationError?: IRegulatoryRejection[];

  productGovernanceAssessment?: ProductGovernance;

  isInternalUser: boolean;
  isProductGovernanceRequired: boolean;

  isPGTimeout: boolean;

  hasProductGovernance: boolean;

  hasPGErrors: boolean;
  hasPGAssErrors: boolean;

  pgTimeoutMs: number;
}

export const ProductGovernanceRegulatoryComponent: FC<ProductGovernanceRegulatoryComponentProps> = ({
  tabId,
  workspaceType,
  isProductGovernanceRequired,
  isProductGovernanceLoading,
  isInError,
  isProductGovernanceAssessmentInError,
  isPGTimeout,
  pgTimeoutMs,
  isInternalUser,
  productGovernanceAssessment,
  productGovernanceDocumentation,
  productGovernanceDocumentationError,
  hasPGErrors,
  hasPGAssErrors,
  hasProductGovernance,
}) => {
  const { formatMessage } = useIntl();
  const isNextDesignEnabled = useToggleFeature('nextDocumentParis');
  const pgTimeoutMsg = useMemo(
    () =>
      formatMessage(
        { id: 'quote.errorMessages.regulationTimeout' },
        { name: 'TargetMarket', minutes: (pgTimeoutMs / 60_000).toFixed(1) },
      ),
    [formatMessage, pgTimeoutMs],
  );

  if (!isProductGovernanceRequired) {
    return null;
  }

  return (
    <RegulatoryComponentWrapper
      componentId="product-governance-accordion"
      title="blotterPanel.PGDocumentAccordionTitle"
      isLoading={isProductGovernanceLoading}
      isInError={isInError}
      errorMessage={isPGTimeout ? pgTimeoutMsg : undefined}
    >
      {hasProductGovernance && (
        <>
          {isInternalUser && (
            <>
              {productGovernanceAssessment && !hasPGAssErrors && (
                <Suspense fallback={<LazyFallback />}>
                  <MifidLazy
                    tabId={tabId}
                    productGovernanceAssessment={productGovernanceAssessment}
                    containerId="side-panel-regulation"
                  />
                </Suspense>
              )}
              {isProductGovernanceAssessmentInError && productGovernanceAssessment?.rejections && (
                <DocumentRejectionErrorPanel
                  containerId="pgass-error"
                  rejections={productGovernanceAssessment?.rejections!}
                  isInternal={isInternalUser}
                />
              )}
            </>
          )}
          <div data-e2e="product-governance-documents">
            <h5>
              <FormattedMessage id="blotterPanel.targetMarket" />
            </h5>
            {productGovernanceDocumentation &&
              (isNextDesignEnabled ? (
                <div className="mt-3">
                  <ProductGovernanceDocuments
                    tabId={tabId}
                    workspaceType={workspaceType}
                    className="mt-3 border border-secondary"
                  />
                </div>
              ) : (
                <div className="mt-3">
                  <ProductGovernanceDocumentsLegacy tabId={tabId} workspaceType={workspaceType} />
                </div>
              ))}
          </div>
          {hasProductGovernance && productGovernanceAssessment?.rawValueForMoney && (
            <div data-e2e="product-governance-vfm" className="mt-4">
              <h5>
                <FormattedMessage id="blotterPanel.valueForMoney" />
              </h5>
              <ValueForMoney
                className="mt-1"
                tabId={tabId}
                containerId="raw-value-for-money"
                rawValueForMoney={productGovernanceAssessment.rawValueForMoney}
              />
            </div>
          )}
        </>
      )}
      {hasPGErrors && productGovernanceDocumentationError && (
        <DocumentRejectionErrorPanel
          containerId="pgdoc-error"
          rejections={productGovernanceDocumentationError}
          isInternal={isInternalUser}
          titleIntKey="errorDetails.pgDocument.standardMessage"
        />
      )}
    </RegulatoryComponentWrapper>
  );
};

ProductGovernanceRegulatoryComponent.displayName = 'ProductGovernanceRegulatoryComponent';
